import _ from "lodash";
import {
  FIREBASE_COLLECTION_GAMES,
  FIREBASE_COLLECTION_PLAYERS,
  FIREBASE_COLLECTION_QUESTIONS,
  FIREBASE_COLLECTION_SUBMISSIONS,
  FIREBASE_COLLECTION_SUPERLATIVES,
  FIREBASE_COLLECTION_NARRATIONS,
} from "./constants";
import logging from "utils/logging";

const log = logging.getLogger("firebaseModels");

const loadUserModelForFirebaseUser = (user) => {
  const u = _.pick(user, [
    "uid",
    "displayName",
    "email",
    "emailVerified",
    "isAnonymous",
    "photoURL",
  ]);

  log.debug("Loaded user model for user", u);

  if (_.isEmpty(u)) {
    return null;
  }

  return u;
};

const loadModelForDocumentSnapshot = (snapshot) => {
  const docType = snapshot.ref.parent.id;
  switch (docType) {
    case FIREBASE_COLLECTION_GAMES:
      return loadGameFromSnapshot(snapshot);
    case FIREBASE_COLLECTION_QUESTIONS:
      return loadQuestionFromSnapshot(snapshot);
    case FIREBASE_COLLECTION_PLAYERS:
      return loadPlayerFromSnapshot(snapshot);
    case FIREBASE_COLLECTION_SUBMISSIONS:
      return loadSubmissionFromSnapshot(snapshot);
    case FIREBASE_COLLECTION_SUPERLATIVES:
      return loadSuperlativeFromSnapshot(snapshot);
    case FIREBASE_COLLECTION_NARRATIONS:
      return loadNarrationFromSnapshot(snapshot);
    default:
      return null;
  }
};

const loadModelsForQuerySnapshot = (snapshot) => {
  if (snapshot.empty) {
    return [];
  }

  return snapshot.docs.map((docSnapshot) =>
    loadModelForDocumentSnapshot(docSnapshot)
  );
};

const loadDiffOfModelsForQuerySnapshot = (snapshot) => {
  // Return an object of normalized values within { added: [], deleted: [], updated: [] }
  let result = {
    added: [],
    deleted: [],
    updated: [],
  };

  const docChanges = snapshot.docChanges();
  docChanges.forEach((docChange) => {
    const changeType = docChange.type;
    const snapshot = docChange.doc;
    const model = loadModelForDocumentSnapshot(snapshot);
    
    switch (changeType) {
      case "added":
        result.added.push(model);
        break;
      case "modified":
        result.updated.push(model);
        break;
      case "removed":
        result.deleted.push(model);
        break;
      default:
        break;
    }
  });

  return result;
};

const convertTimestamps = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (obj.toDate && typeof obj.toDate === 'function') {
    return obj.toDate().toJSON();
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertTimestamps(item));
  }

  if (typeof obj === 'object') {
    const result = {};
    for (const key in obj) {
      result[key] = convertTimestamps(obj[key]);
    }
    return result;
  }

  return obj;
};

const loadGameFromSnapshot = (snapshot) => {
  const data = snapshot.data();

  if (!data) {
    return null;
  }

  const convertedData = convertTimestamps(data);
  return {
    ...convertedData,
    id: snapshot.id,
    // docRef: snapshot.ref,
  };
};

const loadQuestionFromSnapshot = (snapshot) => {
  const data = snapshot.data();
  const gameId = snapshot.ref.parent.parent.id;
  const { startTime, ...rest } = data;

  return {
    ...rest,
    id: snapshot.id,
    gameId: gameId,
    startTime: startTime ? startTime.toDate().toJSON() : null,
  };
};

const loadSubmissionFromSnapshot = (snapshot) => {
  const data = snapshot.data();
  const questionId = snapshot.ref.parent.parent.id;
  const gameId = snapshot.ref.parent.parent.parent.parent.id;
  const { submittedAt, ...rest } = data;

  return {
    ...rest,
    id: snapshot.id,
    questionId,
    gameId,
    submittedAt: submittedAt ? submittedAt.toDate().toJSON() : null,
  };
};

const loadPlayerFromSnapshot = (snapshot) => {
  const data = snapshot.data();
  const gameId = snapshot.ref.parent.parent.id;
  return {
    ...data,
    id: snapshot.id,
    gameId: gameId,
  };
};

const loadNarrationFromSnapshot = (snapshot) => {
  const data = snapshot.data();
  const gameId = snapshot.ref.parent.parent.id;

  const convertedData = convertTimestamps(data);
  return {
    ...convertedData,
    id: snapshot.id,
    gameId: gameId,
  };
};

const loadSuperlativeFromSnapshot = (snapshot) => {
  const data = snapshot.data();
  const gameId = snapshot.ref.parent.parent.id;
  return {
    ...data,
    id: snapshot.id,
    gameId: gameId,
  };
};

export {
  loadUserModelForFirebaseUser,
  loadModelForDocumentSnapshot,
  loadModelsForQuerySnapshot,
  loadDiffOfModelsForQuerySnapshot,
};
